import type { LatLng } from '~/_shared/types/latLng';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  isDriveTimePolygon, isGroupRadius, isIndividualRadius,
} from '~/_shared/types/proximity/proximity.types';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { getProximityRadius } from '~/_shared/utils/proximity/getProximityRadius';
import { getDriveTimePolygonShortLabel } from '~/proximity/proximity.helpers';
import { proximityListSelector } from '~/proximity/useProximities';
import { getBoundaryStateItemSelector } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryStateItem';
import { getBoundaryTerritoryDisplayNameSelector } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import {
  getBoundaryFilterRequestSelector, getBoundaryTerritoryFilterGroupsSelector,
} from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useMarkersInBoundary';
import type { ActiveProximity } from '~/sidebar/sidebarApps/rightSidebar/rightSidebarConfiguration';
import type { BoundaryFilterBoundariesRequest } from '~/spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import type { BoundaryTerritoryFilterGroups } from '~/spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import type { RadiusFilterRequestArguments } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import type { AppState } from '~/store/app.store';
import type {
  BoundaryGroupId,
  BoundaryId, BoundaryTerritoryGroupId,
  BoundaryTerritoryIdentifier,
  ExtendedBoundaryIdentifier,
} from '~/store/boundaries/boundaryIdentifier.type';
import { selectLatLngSpreadsheetData } from '~/store/selectors/spreadsheetDataMemoizedSelectors';
import type { AreaRequestGetterFilterParams } from '~/store/spreadsheetData/area/spreadsheetData.area.helpers';
import { translate } from '~/translations/Trans';

export type SpreadsheetDataEntityFilterState = Readonly<{
  boundaries: ReadonlyArray<ExtendedBoundaryIdentifier>;
  boundaryTerritories: ReadonlyArray<BoundaryTerritoryIdentifier>;
  proximities: ReadonlyArray<ActiveProximity>;
}>;

export type SpreadsheetDataEntityFilter = Pick<
  AreaRequestGetterFilterParams,
    'circles' | 'multiPolygons' | 'boundaries' | 'boundaryTerritories'
> & {
  names: string[];
  isEmpty: boolean;
};

type SpreadsheetDataEntityFiltersSelectorType = (state: AppState, filter: SpreadsheetDataEntityFilterState) => SpreadsheetDataEntityFilter;
export const spreadsheetDataEntityFiltersSelector: SpreadsheetDataEntityFiltersSelectorType = createStateSelector([
  proximityListSelector,
  // wrapped so its optional second arg doesn't collide with our `filter` arg in reselect's positional param merge
  (state: AppState) => selectLatLngSpreadsheetData(state),
  getBoundaryTerritoryFilterGroupsSelector,
  getBoundaryFilterRequestSelector,
  getBoundaryTerritoryDisplayNameSelector,
  getBoundaryStateItemSelector,
  (_state: AppState, filter: SpreadsheetDataEntityFilterState) => filter,
], (
  proximities,
  latLngData,
  getBoundaryTerritoriesFilter,
  getBoundariesFilter,
  getBoundaryTerritoryDisplayName,
  getBoundaryStateItem,
  filter,
): SpreadsheetDataEntityFilter => {
  const names: string[] = [];
  const circles: RadiusFilterRequestArguments[] = [];
  const multiPolygons: MultiPolygon[] = [];
  const boundaries: BoundaryFilterBoundariesRequest[] = [];
  const boundaryTerritories: BoundaryTerritoryFilterGroups[] = [];

  filter.proximities.forEach((filteredProximity) => {
    const proximityById = proximities.find((proximity) => proximity.id === filteredProximity.proximityId);
    if (!proximityById) {
      return;
    }

    if (isDriveTimePolygon(proximityById) && proximityById.data.paths) {
      names.push(translate('Drive Time Polygon: {{time}}', {
        time: getDriveTimePolygonShortLabel(proximityById.data.hours, proximityById.data.minutes),
      }));
      multiPolygons.push(proximityById.data.paths);
    }

    if (isGroupRadius(proximityById) || isIndividualRadius(proximityById)) {
      const specificRowId = filteredProximity.spreadsheetRowId;

      let latLngIfGroupProximity: LatLng | undefined = undefined;
      if (isGroupRadius(proximityById) && specificRowId) { // we only support 1 circle, so if group, specificRowId needs to be set
        latLngIfGroupProximity = latLngData.getRow(specificRowId);
      }
      const latLng = isIndividualRadius(proximityById)
        ? proximityById.data.data
        : latLngIfGroupProximity;

      const radialDistance = getProximityRadius(proximityById, specificRowId);

      if (radialDistance && latLng) {
        names.push(translate('Proximity Radius{{subtype}}: {{distance}}{{unit}}', {
          subtype: '',
          distance: radialDistance.radius,
          unit: translate(radialDistance.unit),
        }));
        circles.push({
          center: latLng,
          ...radialDistance,
        });
      }
    }
  });

  const boundaryIdsByBoundaryGroup = new Map<BoundaryGroupId, { ids: BoundaryId[]; btgId: BoundaryTerritoryGroupId }>();
  filter.boundaries.forEach((boundary) => {
    const entry = boundaryIdsByBoundaryGroup.get(boundary.boundaryGroupId) ?? { ids: [], btgId: boundary.boundaryTerritoryGroupId };
    entry.ids.push(boundary.boundaryId);
    boundaryIdsByBoundaryGroup.set(boundary.boundaryGroupId, entry);
  });
  boundaryIdsByBoundaryGroup.forEach((boundaryIds, boundaryGroupId) => {
    const boundaryFilter = getBoundariesFilter(boundaryIds.btgId, boundaryIds.ids);
    if (boundaryFilter) {
      names.push(`${translate('Boundary')} | ${boundaryIds.ids.map((boundaryId) =>
        getBoundaryStateItem(boundaryId, boundaryGroupId)?.displayName ?? translate('Unknown')
      ).join(', ')}`);
      boundaries.push(boundaryFilter.boundaries);
    }
  });

  filter.boundaryTerritories.forEach((boundaryTerritory) => {
    const boundaryTerritoryFilter = getBoundaryTerritoriesFilter(
      boundaryTerritory.boundaryTerritoryGroupId, boundaryTerritory.boundaryTerritoryId
    );
    if (boundaryTerritoryFilter) {
      const btgName = getBoundaryTerritoryDisplayName(boundaryTerritory.boundaryTerritoryId, boundaryTerritory.boundaryTerritoryGroupId);
      names.push(`${translate('Territory')} | ${btgName}`);
      boundaryTerritories.push(...boundaryTerritoryFilter);
    }
  });

  const isEmpty = names.length === 0;

  return {
    names,
    circles: circles.length > 0 ? circles : undefined,
    multiPolygons: multiPolygons.length > 0 ? multiPolygons : undefined,
    boundaries: boundaries[0] ? boundaries[0] : undefined, //for now we only support one BG
    boundaryTerritories: boundaryTerritories.length > 0 ? boundaryTerritories : undefined,
    isEmpty,
  };
});

export const useSpreadsheetDataEntityFiltersSelector = ({
  filter,
}: Readonly<{
  filter: SpreadsheetDataEntityFilterState;
}>): SpreadsheetDataEntityFilter =>
  useSelector((state) => spreadsheetDataEntityFiltersSelector(state, filter));
